import type { NextRequest } from "next/server";
import { 
  extractUserEmailFromJwt,
  extractUserIdFromJwt,
  verifySupabaseJwtAndGetUser
} from './supabase-auth-working';
import { 
  getClientIP as getClientIPFromSupabase,
  getUserAgent as getUserAgentFromSupabase
} from './supabase-auth';
import { DebugLogger } from './debug-logger';

// Enhanced auth functions with debug logging
export const extractUserEmailFromToken = async (request: NextRequest): Promise<string | null> => {
  DebugLogger.logAuth("extractUserEmailFromToken", "WRAPPER START", request);
  
  DebugLogger.logAuth('extractUserEmailFromToken', { 
    hasRequest: !!request,
    url: request?.url,
    hasAuthHeader: !!request?.headers.get('authorization')
  }, request);
  
  DebugLogger.logAuth("extractUserEmailFromToken", "Calling extractUserEmailFromJwt", request);
  const result = await extractUserEmailFromJwt(request);
  DebugLogger.logAuth("extractUserEmailFromToken", { extractUserEmailFromJwtReturned: result }, request);
  
  DebugLogger.logAuth('extractUserEmailFromToken_result', { 
    success: !!result,
    email: result ? '[EMAIL_PRESENT]' : '[EMAIL_MISSING]'
  }, request);
  
  DebugLogger.logAuth("extractUserEmailFromToken", "WRAPPER END", request);
  
return result;
};

export const extractUserIdFromToken = async (request: NextRequest): Promise<string | null> => {
  DebugLogger.logAuth('extractUserIdFromToken', { 
    hasRequest: !!request,
    url: request?.url,
    hasAuthHeader: !!request?.headers.get('authorization')
  }, request);
  
  const result = await extractUserIdFromJwt(request);
  
  DebugLogger.logAuth('extractUserIdFromToken_result', { 
    success: !!result,
    userId: result ? '[USER_ID_PRESENT]' : '[USER_ID_MISSING]'
  }, request);
  
  return result;
};

export const extractUserTypeFromToken = async (request: NextRequest): Promise<string | null> => {
  DebugLogger.logAuth('extractUserTypeFromToken', { 
    hasRequest: !!request,
    url: request?.url,
    hasAuthHeader: !!request?.headers.get('authorization')
  }, request);
  
  // Use the full verification function to get user type
  const result = await verifySupabaseJwtAndGetUser(request);
  
  if (result instanceof Response) {
    // If it's an error response, return null
    DebugLogger.logAuth('extractUserTypeFromToken_result', { 
      success: false,
      userType: '[USER_TYPE_MISSING]'
    }, request);
    
return null;
  }
  
  const userType = result.role || null;
  
  DebugLogger.logAuth('extractUserTypeFromToken_result', { 
    success: !!userType,
    userType: userType || '[USER_TYPE_MISSING]'
  }, request);
  
  return userType;
};

export const validateAuthHeader = async (request: NextRequest): Promise<boolean> => {
  DebugLogger.logAuth('validateAuthHeader', { 
    hasRequest: !!request,
    url: request?.url,
    hasAuthHeader: !!request?.headers.get('authorization')
  }, request);
  
  // Use the working JWT verification function
  const result = await verifySupabaseJwtAndGetUser(request);
  const isValid = !(result instanceof Response);
  
  DebugLogger.logAuth('validateAuthHeader_result', { 
    success: isValid,
    isValid: isValid
  }, request);
  
  return isValid;
};

/**
 * Get client IP address from request
 */
export function getClientIP(request: NextRequest): string | null {
  DebugLogger.logAuth('getClientIP', { 
    hasRequest: !!request,
    url: request?.url
  }, request);
  
  const result = getClientIPFromSupabase(request);
  
  DebugLogger.logAuth('getClientIP_result', { 
    success: !!result,
    ip: result || '[IP_MISSING]'
  }, request);
  
  return result;
}

/**
 * Get user agent from request
 */
export function getUserAgent(request: NextRequest): string | null {
  DebugLogger.logAuth('getUserAgent', { 
    hasRequest: !!request,
    url: request?.url
  }, request);
  
  const result = getUserAgentFromSupabase(request);
  
  DebugLogger.logAuth('getUserAgent_result', { 
    success: !!result,
    userAgent: result ? '[USER_AGENT_PRESENT]' : '[USER_AGENT_MISSING]'
  }, request);
  
  return result;
}
